import { createSelector } from 'reselect';
import { TwinData } from '../config/types';
import {
  getAssetsArray,
  getFilteredAssetsKeyedByStatusForDigitalTwin,
  getPlannedTwinIDsKeyedByTwinId,
  getSelectedAssetType,
  getSelectedAssets,
  getBandsKeyedById,
  getFilteredAssetKeyedByTwinId,
  getAssetManagerAssetSelection,
  getTimeTravelAssetsById,
  getAssetManagerSelectedPlanId,
  getAssetManagerShowPlanView,
} from '.';
import map from './map';
import { getFadedColor } from '../utils/colors';
import { getPlanResultFields } from '../utils/plannedTwinId';

export const getTwinIdsKeyedByAssetId = createSelector(
  [map.assetsKeyedById, getSelectedAssetType, getAssetsArray],
  (assetsKeyedById, selectedAssetType, assetArray) => {
    const { slots, modelIdsSlotIdReference } = selectedAssetType || {};

    if (modelIdsSlotIdReference) {
      const slotsKeyById = new Map(slots?.map(slot => [slot.id, slot]));
      const currentSlot = slotsKeyById.get(modelIdsSlotIdReference);
      const relationship = currentSlot?.relationship;

      const twinArray = assetArray
        .map((asset) => {
          const { id } = asset;
          const assetSlots = asset?.slots || {};
          const slot = assetSlots[relationship === 'has_many' ? 'children' : 'parents'];
          const assetIdsFromSlot = slot?.[modelIdsSlotIdReference];
          const twinFromSlot = typeof assetIdsFromSlot === 'string'
            ? [assetIdsFromSlot]
            : assetIdsFromSlot
              ?.map(assetId => assetsKeyedById.get(assetId)?.destination?.twinID || [])
              ?.flat();
          return { id, twinID: twinFromSlot };
        })
        .filter(({ twinID }) => twinID);

      return new Map(twinArray.map(twin => [twin.id, twin]));
    }

    const twinArray = assetArray
      .map((asset) => {
        const { destination, id } = asset;
        const twinID = destination?.twinID;
        return { id, twinID };
      })
      .filter(({ twinID }) => twinID);
    return new Map(twinArray.map(twin => [twin.id, twin]));
  },
);

export const getFilteredTwinData = createSelector(
  [
    getSelectedAssetType,
    getFilteredAssetsKeyedByStatusForDigitalTwin,
    map.lastFetchStartTime,
    getTwinIdsKeyedByAssetId,
  ],
  (
    selectedAssetType,
    filteredAssetsKeyedByStatus,
    lastFetchStartTime,
    twinIdsKeyedByAssetId,
  ): TwinData | undefined => {
    const { statuses, modelIdsSlotIdReference } = selectedAssetType;
    const hexColorsKeyedByTwinId: TwinData['hexColorsKeyedByTwinId'] = {};
    statuses?.forEach(({ id, color }) => {
      const assets = filteredAssetsKeyedByStatus.get(id);
      assets?.forEach((asset) => {
        const twinFromReference = modelIdsSlotIdReference
          ? twinIdsKeyedByAssetId.get(asset.id || '')?.twinID?.[0]
          : asset?.destination?.twinID?.[0];

        if (twinFromReference === undefined || !color) return;
        hexColorsKeyedByTwinId[twinFromReference] = color;
      });
    });

    return {
      hexColorsKeyedByTwinId,
      lastFetched: lastFetchStartTime,
    };
  },
);

export const getFilteredTwinPlan = createSelector(
  [
    getFilteredAssetKeyedByTwinId,
    getBandsKeyedById,
    getPlannedTwinIDsKeyedByTwinId,
    getAssetManagerSelectedPlanId,
    map.lastFetchStartTime,
  ],
  (
    filteredAssetKeyedByTwinId,
    bandKeyedById,
    plannedTwinIdsKeyedByTwinId,
    selectedPlanId,
    lastFetchStartTime,
  ): TwinData | undefined => {
    const hexColorsKeyedByTwinId = {};
    filteredAssetKeyedByTwinId.forEach((_, key) => {
      const plan = plannedTwinIdsKeyedByTwinId
        ?.get(key)
        ?.find(plannedTwinId => plannedTwinId.planID === selectedPlanId);
      const bandID = getPlanResultFields(plan?.result).bandID || '';
      const band = bandKeyedById.get(bandID);
      hexColorsKeyedByTwinId[key] = band?.color || '#e1e1e1';
    });

    return {
      hexColorsKeyedByTwinId,
      lastFetched: lastFetchStartTime,
    };
  },
);

export const getTwinData = createSelector(
  [
    getAssetManagerShowPlanView,
    getFilteredTwinPlan,
    getFilteredTwinData,
    getAssetManagerAssetSelection,
    getTimeTravelAssetsById,
  ],
  (
    showPlanView,
    filteredTwinPlan,
    twinData,
    assetManagerAssetSelection,
    timeTravelAssetsById,
  ): TwinData | undefined => {
    const {
      hoveredAssetId,
      hoverOrigin,
    } = assetManagerAssetSelection;

    const selectData = () => {
      if (showPlanView) return filteredTwinPlan;
      return twinData;
    };
    const data = { ...selectData() };
    if (hoveredAssetId && hoverOrigin === 'webhq' && data?.hexColorsKeyedByTwinId) {
      const hoveredTwinId = timeTravelAssetsById.get(hoveredAssetId)?.destination?.twinID?.[0];
      const newHexColorsKeyedByTwinId: TwinData['hexColorsKeyedByTwinId'] = {};
      Object.keys(data?.hexColorsKeyedByTwinId || {}).forEach((key) => {
        const originalColor = data?.hexColorsKeyedByTwinId?.[key] ?? '#FFFFFF';
        newHexColorsKeyedByTwinId[key] = key === hoveredTwinId
          ? originalColor
          : getFadedColor(originalColor);
      });
      data.hexColorsKeyedByTwinId = newHexColorsKeyedByTwinId;
    }
    return data as TwinData;
  },
);

export const getSelectedTwinIds = createSelector(
  [getSelectedAssets, getTwinIdsKeyedByAssetId],
  (selectedAssets, twinIdsKeyedByAssetId) => selectedAssets
    .map(selected => twinIdsKeyedByAssetId.get(selected?.id)?.twinID?.[0])
    .filter(twin => !!twin) as Array<string>,
);
